import * as React from "react";
import {
    List, Datagrid, TextField, BooleanField, DateField, Edit, SimpleForm, TextInput, BooleanInput,
    Create, ReferenceInput, SelectInput, ArrayInput, SimpleFormIterator, FormDataConsumer,
    NumberInput, useDataProvider,
} from 'react-admin';
import { Box, Typography } from '@material-ui/core';
import { GenerateMappingButton } from './components/generate-mapping-button';
import { DepartmentsSelectInput } from './components/department-select-input';

const ContractTemplateTitle = props => {
    return <span>{props.record ? `${props.record.name}` : ''}</span>;
};

const documentFormatChoices = [
    { id: 'pdf', name: 'PDF' },
    { id: 'docx', name: 'Word (.docx)' },
];

const taxStatusChoices = [
    { id: 'paye', name: 'PAYE' },
    { id: 'ltdCo', name: 'Ltd Co' },
    { id: 'soleTrader', name: 'Sole Trader' },
];

const contractTypeChoices = [
    { id: 'daily', name: 'Daily' },
    { id: 'contract', name: 'Contract' },
];

const recipientRoleChoices = [
    { id: 'signer', name: 'Signer' },
    { id: 'approver', name: 'Approver' },
];

const recipientSourceChoices = [
    { id: 'production_fixed', name: 'Fixed (set here)' },
    { id: 'employment', name: 'From employment' },
    { id: 'send_time_custom', name: 'Entered when sending' },
];

// Known dot-paths resolveField() supports today. Keep this in sync with
// PlaceMappingResolver if new fields are added there.
const employmentFieldChoices = [
    { id: 'employment.name', name: 'Crew member — name' },
    { id: 'employment.email', name: 'Crew member — email' },
    { id: 'employment.agent_name', name: 'Agent — name' },
    { id: 'employment.agent_email', name: 'Agent — email' },
];

// ---------------------------------------------------------------------------
// Place Mapping editor
// ---------------------------------------------------------------------------

const placeSourceChoices = [
    { id: 'field', name: 'Employment field' },
    { id: 'static', name: 'Static value' },
    { id: 'eav', name: 'Custom attribute (EAV)' },
    { id: 'choice', name: 'Yes/No choice' },
    { id: 'computed', name: 'Computed value' },
    { id: 'penalty_rate', name: 'Penalty rate' },
    { id: 'contract_notes', name: 'Contract notes' },
    { id: 'signer', name: 'Signature' },
    { id: 'signer_date', name: 'Signature date' },
    { id: 'signer_input', name: 'Fillable field (signer fills in)' },
];

const CUSTOM_FIELD_VALUE = '__custom__';

// Curated from PlaceMappingResolver::resolveField's known-safe dot-paths and
// existing templates' place mappings. "Custom" falls back to freeform entry
// for anything not listed here.
const employmentDotPathChoices = [
    { id: 'employment.name', name: 'Full name' },
    { id: 'employment.first_name', name: 'First name' },
    { id: 'employment.surname', name: 'Surname' },
    { id: 'employment.address1', name: 'Address line 1' },
    { id: 'employment.address2', name: 'Address line 2' },
    { id: 'employment.city', name: 'City' },
    { id: 'employment.postcode', name: 'Postcode' },
    { id: 'employment.country', name: 'Country' },
    { id: 'employment.employment_start_date', name: 'Employment start date' },
    { id: 'employment.employment_end_date', name: 'Employment end date' },
    { id: 'employment.contract_type', name: 'Contract type (daily/contract)' },
    { id: 'employment.rate_type', name: 'Rate type (daily/hourly)' },
    { id: 'employment.hire_type', name: 'Hire type (local/distant hire)' },
    { id: 'employment.pre_prep_total_days', name: 'Pre-prep total days' },
    { id: 'employment.prep_total_days', name: 'Prep total days' },
    { id: 'employment.shoot_total_days', name: 'Shoot total days' },
    { id: 'employment.wrap_total_days', name: 'Wrap total days' },
    { id: 'employment.total_days_left', name: 'Remaining days' },
    { id: 'employment.days_per_week', name: 'Days per week' },
    { id: 'employment.hours_per_week', name: 'Hours per week' },
    { id: 'employment.daily_basic_hours', name: 'Daily basic hours' },
    { id: 'employment.weekly_rate_gross', name: 'Weekly rate (gross)' },
    { id: 'employment.weekly_rate_net', name: 'Weekly rate (net)' },
    { id: 'employment.weekly_rate_hol', name: 'Weekly rate (holiday)' },
    { id: 'employment.weekly_gross_ach', name: 'Weekly ACH (gross)' },
    { id: 'employment.weekly_net_ach', name: 'Weekly ACH (net)' },
    { id: 'employment.weekly_hol_ach', name: 'Weekly ACH (holiday)' },
    { id: 'employment.agent_address', name: 'Agent address' },
    { id: 'employment.employment_type', name: 'Employment type (PAYE/Ltd Co/Sole Trader)' },
    { id: 'employment.ltd_co_name', name: 'Ltd Co name' },
    { id: 'employment.vehicle_model', name: 'Vehicle model' },
    { id: 'employment.vehicle_reg', name: 'Vehicle registration' },
    { id: 'employment.job_title', name: 'Job title' },
    { id: 'employment.department.name', name: 'Department' },
    { id: 'employment.email', name: 'Email' },
    { id: 'employment.phone', name: 'Phone' },
    { id: 'employment.agent_name', name: 'Agent name' },
    { id: 'employment.agent_email', name: 'Agent email' },
    { id: 'employment.prod_meeting', name: 'Prod meeting' },
    { id: 'employment.read_through', name: 'Read through' },
    { id: 'employment.pre_prep_start_date', name: 'Pre-prep start date' },
    { id: 'employment.pre_prep_end_date', name: 'Pre-prep end date' },
    { id: 'employment.pre_prep_days', name: 'Pre-prep days' },
    { id: 'employment.pre_prep_weeks', name: 'Pre-prep weeks' },
    { id: 'employment.prep_start_date', name: 'Prep start date' },
    { id: 'employment.prep_end_date', name: 'Prep end date' },
    { id: 'employment.prep_days', name: 'Prep days' },
    { id: 'employment.prep_weeks', name: 'Prep weeks' },
    { id: 'employment.shoot_start_date', name: 'Shoot start date' },
    { id: 'employment.shoot_end_date', name: 'Shoot end date' },
    { id: 'employment.shoot_days', name: 'Shoot days' },
    { id: 'employment.shoot_weeks', name: 'Shoot weeks' },
    { id: 'employment.wrap_start_date', name: 'Wrap start date' },
    { id: 'employment.wrap_end_date', name: 'Wrap end date' },
    { id: 'employment.wrap_days', name: 'Wrap days' },
    { id: 'employment.wrap_weeks', name: 'Wrap weeks' },
    { id: 'employment.total_days', name: 'Total days' },
    { id: 'employment.total_weeks', name: 'Total weeks' },
    { id: 'employment.daily_rate_gross', name: 'Daily rate (gross)' },
    { id: 'employment.daily_rate_net', name: 'Daily rate (net)' },
    { id: 'employment.daily_rate_holiday', name: 'Daily rate (holiday)' },
    { id: 'employment.hourly_rate_gross', name: 'Hourly rate (gross)' },
    { id: 'employment.hourly_rate_net', name: 'Hourly rate (net)' },
    { id: 'employment.hourly_rate_hol', name: 'Hourly rate (holiday)' },
    { id: CUSTOM_FIELD_VALUE, name: 'Custom (enter manually)…' },
];

// Only resolver PlaceMappingResolver::resolveComputed() currently supports —
// add here if a new one is added server-side.
const computedResolverChoices = [
    { id: 'shoot_period_total', name: 'Shoot period total (daily + ACH if applicable)' },
];

const rateVariantChoices = [
    { id: 'gross', name: 'Gross' },
    { id: 'net', name: 'Net' },
    { id: 'holiday', name: 'Holiday' },
];

const signerTypeChoices = [
    { id: 'signature', name: 'Signature' },
    { id: 'initials', name: 'Initials' },
];

const PenaltyTypeCodeSelect = ({ productionId, source, record }) => {
    const dataProvider = useDataProvider();
    const [choices, setChoices] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!productionId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        dataProvider.getList('production-penalty-types', {
            filter: { production_id: productionId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'ASC' },
        })
            .then(({ data }) => {
                setChoices(data.map(pt => ({ id: pt.code, name: pt.full_name ?? pt.code })));
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [dataProvider, productionId]);

    if (!productionId) {
        return (
            <TextInput
                source={source}
                label="Penalty type code"
                record={record}
                helperText="Set a production on this template first to pick from a list"
            />
        );
    }

    return (
        <SelectInput
            source={source}
            label="Penalty type"
            choices={choices}
            record={record}
            disabled={loading}
            helperText={loading ? 'Loading penalty types…' : undefined}
        />
    );
};

const ContractTemplateForm = props => (
    <SimpleForm {...props}>
        <TextInput source="production_id" style={{ display: 'none' }} />
        <TextInput source="name" />
        <TextInput source="signatureapi_template_url" label="SignatureAPI Template URL" fullWidth />
        <SelectInput source="document_format" label="Document Format" choices={documentFormatChoices} />
        <BooleanInput source="is_default" label="Default template for this production" />

        <BooleanInput source="is_hod" label="Only for Heads of Department" />
        <BooleanInput source="is_trainee" label="Only for trainees" />
        <SelectInput
            source="tax_status"
            label="Tax status"
            choices={taxStatusChoices}
            allowEmpty
            helperText="Leave blank to match any tax status"
        />
        <SelectInput
            source="contract_type"
            label="Contract type"
            choices={contractTypeChoices}
            allowEmpty
            helperText="Leave blank to match any contract type"
        />

        <FormDataConsumer>
            {({ formData }) => (
                <DepartmentsSelectInput
                    source="department_ids"
                    productionId={formData.production_id}
                    label="Departments (leave empty to apply to all)"
                    fullWidth
                />
            )}
        </FormDataConsumer>

        <ArrayInput source="recipients" label="Recipients">
            <SimpleFormIterator>
                <TextInput source="key" label="Key" helperText="Matches the {{key}} placeholder on the template" />
                <SelectInput source="role" label="Role" choices={recipientRoleChoices} />
                <SelectInput source="source" label="Source" choices={recipientSourceChoices} />

                <FormDataConsumer>
                    {({ scopedFormData, getSource }) => {
                        if (!scopedFormData) return null;

                        if (scopedFormData.source === 'production_fixed') {
                            return (
                                <>
                                    <TextInput source={getSource('name')} label="Name" record={scopedFormData} />
                                    <TextInput source={getSource('email')} label="Email" record={scopedFormData} />
                                </>
                            );
                        }

                        if (scopedFormData.source === 'employment') {
                            return (
                                <>
                                    <SelectInput
                                        source={getSource('name_field')}
                                        label="Name field"
                                        choices={employmentFieldChoices}
                                        record={scopedFormData}
                                    />
                                    <SelectInput
                                        source={getSource('email_field')}
                                        label="Email field"
                                        choices={employmentFieldChoices}
                                        record={scopedFormData}
                                    />
                                </>
                            );
                        }

                        if (scopedFormData.source === 'send_time_custom') {
                            return (
                                <>
                                    <TextInput
                                        source={getSource('label')}
                                        label="Label shown to production"
                                        record={scopedFormData}
                                    />
                                    <BooleanInput
                                        source={getSource('prefill_from_employment')}
                                        label="Prefill from employment where possible"
                                        record={scopedFormData}
                                    />
                                </>
                            );
                        }

                        return null;
                    }}
                </FormDataConsumer>
            </SimpleFormIterator>
        </ArrayInput>

        <ArrayInput
            source="cc_recipients"
            label="CC on completion"
            helperText="These people are emailed a copy of the signed document once the contract is fully completed. They are not part of the signing flow."
        >
            <SimpleFormIterator>
                <TextInput source="label" label="Label" helperText="e.g. Payroll, Production Office" />
                <TextInput source="email" label="Email" type="email" />
            </SimpleFormIterator>
        </ArrayInput>

        <GenerateMappingButton />

        <ArrayInput source="place_mapping.places" label="Places">
            <SimpleFormIterator>
                <TextInput source="key" label="Key" helperText="Matches the {{key}} placeholder in the document" />
                <SelectInput source="source" label="Type" choices={placeSourceChoices} />

                <FormDataConsumer>
                    {({ scopedFormData, getSource, formData }) => {
                        if (!scopedFormData) return null;
                        const source = scopedFormData.source;

                        if (source === 'field' || source === 'choice') {
                            const usingCustom = scopedFormData.field === CUSTOM_FIELD_VALUE
                                || (scopedFormData.field && !employmentDotPathChoices.some(c => c.id === scopedFormData.field));

                            return (
                                <>
                                    <SelectInput
                                        source={getSource('field')}
                                        label="Field"
                                        choices={employmentDotPathChoices}
                                        record={scopedFormData}
                                    />
                                    {usingCustom && (
                                        <TextInput
                                            source={getSource('field')}
                                            label="Custom field path"
                                            record={scopedFormData}
                                            helperText="e.g. employment.some_field — one level into a loaded relation"
                                        />
                                    )}
                                    {source === 'choice' && (
                                        <TextInput
                                            source={getSource('equals')}
                                            label="Equals (outputs Yes if matched, No otherwise)"
                                            record={scopedFormData}
                                        />
                                    )}
                                    <TextInput
                                        source={getSource('date_format')}
                                        label="Date format (optional)"
                                        record={scopedFormData}
                                        helperText="Only used if the field resolves to a date. PHP date() format, e.g. d/m/Y. Defaults to Y-m-d."
                                    />
                                </>
                            );
                        }

                        if (source === 'static') {
                            return <TextInput source={getSource('value')} label="Value" record={scopedFormData} />;
                        }

                        if (source === 'eav') {
                            return (
                                <TextInput
                                    source={getSource('slug')}
                                    label="Attribute slug"
                                    record={scopedFormData}
                                    helperText="Matches a custom attribute slug on the employment"
                                />
                            );
                        }

                        if (source === 'computed') {
                            return (
                                <>
                                    <SelectInput
                                        source={getSource('resolver')}
                                        label="Resolver"
                                        choices={computedResolverChoices}
                                        record={scopedFormData}
                                    />
                                    <SelectInput
                                        source={getSource('variant')}
                                        label="Variant"
                                        choices={rateVariantChoices}
                                        record={scopedFormData}
                                    />
                                </>
                            );
                        }

                        if (source === 'penalty_rate') {
                            return (
                                <>
                                    <PenaltyTypeCodeSelect
                                        productionId={formData.production_id}
                                        source={getSource('penalty_type_code')}
                                        record={scopedFormData}
                                    />
                                    <SelectInput
                                        source={getSource('variant')}
                                        label="Variant"
                                        choices={rateVariantChoices}
                                        record={scopedFormData}
                                    />
                                </>
                            );
                        }

                        if (source === 'contract_notes') {
                            return (
                                <Typography variant="caption" color="textSecondary">
                                    Combines all contract notes for this crew member into one block.
                                </Typography>
                            );
                        }

                        if (source === 'signer' || source === 'signer_date' || source === 'signer_input') {
                            const recipientChoices = (formData.recipients ?? [])
                                .filter(r => r?.key)
                                .map(r => ({ id: r.key, name: r.key }));

                            return (
                                <>
                                    <SelectInput
                                        source={getSource('recipient')}
                                        label="Recipient"
                                        choices={recipientChoices}
                                        record={scopedFormData}
                                        helperText={recipientChoices.length === 0 ? 'Add a recipient above first' : undefined}
                                    />
                                    {source === 'signer' && (
                                        <>
                                            <SelectInput
                                                source={getSource('type')}
                                                label="Mark type"
                                                choices={signerTypeChoices}
                                                record={scopedFormData}
                                            />
                                            <NumberInput
                                                source={getSource('height')}
                                                label="Height (pt, optional)"
                                                record={scopedFormData}
                                                helperText="20–60pt, defaults to 60. Width is set automatically (height × 2.5)."
                                            />
                                        </>
                                    )}
                                    {source === 'signer_date' && (
                                        <TextInput
                                            source={getSource('date_format')}
                                            label="Date format"
                                            record={scopedFormData}
                                            helperText="Defaults to YYYY-MM-DD if left blank"
                                        />
                                    )}
                                    {source === 'signer_input' && (
                                        <BooleanInput
                                            source={getSource('required')}
                                            label="Required"
                                            record={scopedFormData}
                                            defaultValue={true}
                                            helperText="If off, the signer can leave this blank"
                                        />
                                    )}
                                </>
                            );
                        }

                        return null;
                    }}
                </FormDataConsumer>

                <FormDataConsumer>
                    {({ scopedFormData, getSource }) => {
                        if (!scopedFormData) return null;
                        if (['signer', 'signer_date', 'signer_input'].includes(scopedFormData.source)) return null;

                        return (
                            <>
                                <NumberInput source={getSource('font_size')} label="Font size (optional)" record={scopedFormData} />
                                <TextInput source={getSource('font_color')} label="Font color (optional, e.g. #000000)" record={scopedFormData} />
                            </>
                        );
                    }}
                </FormDataConsumer>
            </SimpleFormIterator>
        </ArrayInput>

        <FormDataConsumer>
            {({ formData }) => (
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>Place Mapping Preview (read-only)</Typography>
                    <Box
                        component="pre"
                        p={2}
                        style={{ background: '#f5f5f5', overflowX: 'auto', fontSize: 12, borderRadius: 4 }}
                    >
                        {JSON.stringify(formData.place_mapping, null, 2)}
                    </Box>
                </Box>
            )}
        </FormDataConsumer>
    </SimpleForm>
);

export const ContractTemplateEdit = props => (
    <Edit title={<ContractTemplateTitle />} {...props}>
        <ContractTemplateForm />
    </Edit>
);

export const ContractTemplateCreate = props => (
    <Create {...props}>
        <ContractTemplateForm />
    </Create>
);
